import type { ConflictRecord } from "@hq/client";
import type { Area, Folder, Note, Project, ProjectStatus, Reminder, Task } from "@hq/contracts";
import type { NoteQuery, ReminderQuery, TaskQuery } from "@hq/local-store";
import { type LiveQueryResult, useLiveQuery } from "./useLiveQuery";

export type ProjectQuery = { status?: readonly ProjectStatus[] };

export function useTasks(query?: TaskQuery): LiveQueryResult<Task[]> {
  return useLiveQuery(["tasks"], (s) => s.tasks.list(query), [query]);
}

export function useTask(id: string | undefined): LiveQueryResult<Task | undefined> {
  return useLiveQuery(["tasks"], (s) => (id ? s.tasks.get(id) : Promise.resolve(undefined)), [id]);
}

export function useNotes(query?: NoteQuery): LiveQueryResult<Note[]> {
  return useLiveQuery(["notes"], (s) => s.notes.list(query), [query]);
}

export function useNote(id: string | undefined): LiveQueryResult<Note | undefined> {
  return useLiveQuery(["notes"], (s) => (id ? s.notes.get(id) : Promise.resolve(undefined)), [id]);
}

export function useFolders(): LiveQueryResult<Folder[]> {
  return useLiveQuery(["folders"], (s) => s.folders.all(), []);
}

export function useProjects(query?: ProjectQuery): LiveQueryResult<Project[]> {
  return useLiveQuery(["projects"], (s) => s.projects.list(query), [query]);
}

export function useReminders(query?: ReminderQuery): LiveQueryResult<Reminder[]> {
  return useLiveQuery(["reminders"], (s) => s.reminders.list(query), [query]);
}

export function usePendingMutationCount(): LiveQueryResult<number> {
  return useLiveQuery(["mutations"], (s) => s.mutations.count(), []);
}

/** The local conflict marker the sync engine sets when the server refused a note write. */
export function useNoteConflict(noteId: string): LiveQueryResult<ConflictRecord | undefined> {
  return useLiveQuery(["kv"], (s) => s.kv.get<ConflictRecord>(`conflict:note:${noteId}`), [noteId]);
}

export function useAreas(): LiveQueryResult<Area[]> {
  return useLiveQuery(["areas"], (s) => s.areas.all(), []);
}
